/**
 * Settings store — Phase 2 (LocalSettings + persist + legacy migration).
 *
 * Phase 2 of settings-zustand-migration: the LocalSettings slice + `persist`
 * middleware + a synchronous pre-rehydration migration that copies the 11
 * legacy localStorage keys into the new `va-settings-v1` namespace on first
 * boot. Consumer call sites across 9 files now read/write through this store
 * instead of touching localStorage directly.
 *
 * Plan: voice-assistant/plans/settings-zustand-migration.md
 * Tracking: #1125 (5-phase umbrella; this PR ticks Phase 2)
 *
 * Architecture
 * ────────────
 * Two slices, one store:
 *   - BackendSettings (Phase 1) — server is truth, NOT persisted to localStorage
 *     (would briefly flash stale on 2nd-device sign-in; `loadFromBackend` is fast
 *     enough).
 *   - LocalSettings (this phase)  — device-local by design (theme, TTS voice,
 *     reader prefs, etc.). Persisted via `persist` middleware in a single
 *     namespaced blob, `va-settings-v1`.
 *
 * `partialize` strictly limits what the persist middleware writes — anything in
 * BackendSettings is excluded. Updating that partializer is the ONE place you
 * change to add or remove a persisted local field.
 *
 * Pre-rehydration migration (plan risk R3)
 * ────────────────────────────────────────
 * The persist middleware reads `va-settings-v1` at `create()` time. If the user
 * upgraded from a pre-Phase-2 deploy, their 11 legacy keys exist but the new
 * blob does not — and the middleware would hydrate empty defaults, making
 * settings appear lost.
 *
 * The fix is `runLegacyMigration()` running synchronously at module-init time
 * (before `create()` is invoked). It checks for the new blob; if absent AND any
 * legacy key is present, it builds the blob in the exact `{ state, version }`
 * format the persist middleware expects and writes it to localStorage. Then
 * `create()` runs and `persist` reads the seeded blob.
 *
 * Non-destructive: legacy keys are NOT deleted. If we roll back the deploy, the
 * pre-Phase-2 frontend still reads from legacy and the user sees no loss.
 * Cleanup of legacy keys happens in a follow-up PR after the migration has
 * been live for one release.
 *
 * Whitelist guardrail (R2 — Phase 1)
 * ──────────────────────────────────
 * `BACKEND_MEMO_WHITELIST` mirrors `settings.controller.ts:264-310` and uses
 * `satisfies readonly (keyof UserPreferences)[]` to block typos at compile time.
 * See Phase 1 commit for full rationale.
 */
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector, createJSONStorage } from 'zustand/middleware';
import {
  DEFAULT_USER_PREFERENCES,
  type UserPreferences,
  type UserSettings,
} from '@voice-assistant/shared';
import {
  getSettings,
  saveUserPreferences,
  setGeminiDeepMode as apiSetGeminiDeepMode,
  setSttCorrectionEnabled as apiSetSttCorrectionEnabled,
  setSttCorrectionThinking as apiSetSttCorrectionThinking,
  setPreferWhisper as apiSetPreferWhisper,
  setAutoSubmitVoice as apiSetAutoSubmitVoice,
  recordConsent as apiRecordConsent,
} from '../api/settings';
import { authFetch } from '../api/authFetch';

// ── Whitelist (mirror of settings.controller.ts:264-310) ────────────────────
// MUST stay in lockstep with the controller. `satisfies` blocks typos.
export const BACKEND_MEMO_WHITELIST = [
  'planModel',
  'contextLines',
  'maxPlannerTurns',
  'maxWorkerTurns',
  'maxChatTurns',
  'autoTriage',
  'memoLanguage',
  'autoPin',
  'autoReplanEnabled',
  'autoReplanThreshold',
  'autoDocsUpdate',
  'autoDocsUpdateThreshold',
  'autoContinueOnReset',
  'handoffOnReset',
  'speechProvider',
  'autoPlay',
  'osBackToSessionList',
  'tempDocsDirName',
  'plansDirName',
  'plansSourceDir',
  'projectDocsDirName',
  'registryDirName',
  'archiveDirName',
  'diagramStyle',
  'messageRetentionDays',
  'defaultOpusEffort',
  'defaultSonnetEffort',
  'acknowledgedMaxEffort',
  'default1mContextEnabled',
  'akbContextBudget',
  'semanticContextBudget',
  'embeddingsAbMode',
  'classifierMaxTokens',
  'outputTokenBudget',
] as const satisfies readonly (keyof UserPreferences)[];

export type WhitelistedMemoKey = (typeof BACKEND_MEMO_WHITELIST)[number];
export type WhitelistedMemo = Pick<UserPreferences, WhitelistedMemoKey>;

/**
 * Snapshot of the backend memo slice — captured when SettingsModal mounts (Phase 3b)
 * and used as the canonical "discard target" if the user clicks Discard.
 * Only whitelisted memo fields are captured; non-whitelisted fields
 * are excluded to prevent silently dropping them on restore.
 */
export type BackendMemoSnapshot = Partial<WhitelistedMemo>;

// ── LocalSettings slice (Phase 2) ───────────────────────────────────────────

/**
 * Reader-mode code-block TTS behavior (`getCodeBlockBehavior` in textWalker.ts).
 * - 'skip': silently skip code blocks (default for text-only listening).
 * - 'announce': say "Code block, N lines" placeholder instead of reading code
 *   (default — matches SettingsModal's "Announce" option).
 * - 'full': read the raw code text aloud.
 */
export type ReaderCodeBlocksMode = 'skip' | 'announce' | 'full';

/**
 * Brato theme value — `'B'`/`'t'`/`'a'`/`null`. Same value space as the legacy
 * `brato-theme` localStorage key (see `themeSync.ts:normalizeBratoTheme`).
 */
export type BratoTheme = string | null;

/**
 * Desktop shell density mode (#1657 Phase 5a). Plan §2.2 LOCKED constraint: three modes,
 * persisted per user; density retunes SPACING/TYPE SCALE only (never colors). Consumed by
 * DesktopShell (stamps `data-density` on the `[data-shell="desktop"]` root; desktop.css
 * variable overrides do the rest). Device-local by design → LocalSettings, not backend memo.
 */
export type DesktopDensity = 'compact' | 'comfortable' | 'spacious';

/**
 * Desktop reduced-motion preference (#1804 item 3, plan §2.5 in-app override). `'system'` (the
 * default) leaves the OS `prefers-reduced-motion` in charge; `'reduce'` forces desktop motion
 * off; `'full'` forces it on even when the OS asks for reduction. DesktopShell stamps it as
 * `data-motion` on the `[data-shell="desktop"]` root and desktop.css does the rest — the same
 * data-attr pattern as `data-density`. Device-local → LocalSettings.
 */
export type ReducedMotionPref = 'system' | 'reduce' | 'full';

interface LocalSettings {
  theme: BratoTheme;
  desktopDensity: DesktopDensity;
  reducedMotion: ReducedMotionPref;
  ttsVoiceId: string | null;
  ttsSpeed: number;
  sttLanguage: string;
  readerCodeBlocks: ReaderCodeBlocksMode;
  readerCompact: boolean;
  readerSyntaxHighlight: boolean;
  readerFilePaths: boolean;
  readerSlashCmds: boolean;
  readerEmbedMedia: boolean;
  beetleVisible: boolean;
  wasmEnabled: boolean;
}

const LOCAL_SETTINGS_DEFAULTS: LocalSettings = {
  theme: null,
  desktopDensity: 'comfortable',
  reducedMotion: 'system',
  ttsVoiceId: null,
  ttsSpeed: 1,
  sttLanguage: 'en',
  readerCodeBlocks: 'announce',
  readerCompact: true,
  readerSyntaxHighlight: true,
  readerFilePaths: true,
  readerSlashCmds: true,
  readerEmbedMedia: true,
  beetleVisible: true,
  wasmEnabled: false,
};

// ── Direct-column settings — each has its own dedicated PUT endpoint ────────
// (preferredProvider retired — model-meta-hardening Phase 6b; provider is now
//  derived per-session from the session model, not a per-user default.)
interface DirectColumnSettings {
  browserAlwaysOn: boolean;
  geminiDeepMode: 'off' | 'detected' | 'all';
  sttCorrectionEnabled: boolean | null;
  sttCorrectionThinking: boolean | null;
  preferWhisper: boolean | null;
  autoSubmitVoice: boolean | null;
}

const DIRECT_COLUMN_DEFAULTS: DirectColumnSettings = {
  browserAlwaysOn: false,
  geminiDeepMode: 'detected',
  sttCorrectionEnabled: null,
  sttCorrectionThinking: null,
  preferWhisper: null,
  autoSubmitVoice: null,
};

/** Status flags populated by actions — not user-editable. */
interface ServerOnlyState {
  /** True once loadFromBackend has populated the store from the server. */
  _loaded: boolean;
  /** True while a mutation is in flight. */
  _saving: boolean;
  /** Last error message from any action, or null. UI consumers surface this. */
  _lastError: string | null;
  /** Profile-derived TTS provider (from GET /profile features.tts_provider).
   *  Transient — NOT persisted (server-truth per deployment). When 'browser',
   *  useTTS narrates via window.speechSynthesis instead of fetching server audio. SE-PF7. */
  ttsProvider: string;
  /** Profile-derived whisper_enabled. Transient, not persisted. With ttsProvider it
   *  derives `serverVoiceEnabled` — the gate that hides server-voice Settings controls. SE-PF7. */
  whisperEnabled: boolean;
  /** Illustrative "Auto" turn-budget hint (fresh session, 1M ctx) from
   *  `backend/src/voice/turn-budget.ts`. Transient, server-truth — null until loaded. */
  autoTurnBudgetHint: number | null;
}

// ── Board-edit session FSM ──────────────────────────────────────────────────
//
// The settings board's EDIT SESSION (snapshot → live-apply → dirty → commit →
// discard/restore → guarded close) used to live in SettingsModal.tsx across a
// `useRef` snapshot, a dummy `useState` epoch counter whose only job was forcing
// a re-render after that ref mutated, plus `boardSaving`/`showCloseConfirm`/
// `discardKey`. That is the frontend-FSM pattern's #1 anti-pattern: multiple
// unsynced holders of one logical state. It now lives here as a machine, and the
// modal renders it. See plans/settingsmodal-board-edit-fsm.md.
//
// It lives in THIS store (not a new one) because "Discard & Close" must be ONE
// action — restore the server values AND end the session — and restoreMemoSnapshot
// already lives here. Two store calls for one user intent is a bug even when
// consecutive (pattern doc, element 4).

export type BoardEditPhase = 'idle' | 'pristine' | 'dirty' | 'confirming' | 'reverting' | 'closed';

export type BoardEditTrigger =
  | 'STORE_LOADED'
  | 'CLOSE_REQUESTED'
  | 'FIELD_PATCHED'
  | 'RECONCILED'
  | 'COMMIT_KEPT'
  | 'DISCARD_REQUESTED'
  | 'KEEP_EDITING'
  | 'KEEP_AND_CLOSE'
  | 'DISCARD_AND_CLOSE_REQUESTED'
  | 'REVERT_SUCCEEDED'
  | 'REVERT_SUCCEEDED_CLOSING'
  | 'REVERT_FAILED';

/**
 * R-A1 — the transition matrix as data. This is the single source of truth for
 * the board-edit machine: `applyPhase()` below REJECTS any (from, trigger) pair
 * absent from this table, so the matrix is load-bearing rather than descriptive.
 *
 * setpoint-evals/settings-board-fsm/run-all.sh greps this array's row count
 * against the simulator scenario count; divergence is a build failure. Adding a
 * transition here without a scenario (or vice versa) is mechanically caught.
 *
 * Rows 4 and 8 (`RECONCILED`) are the reason element 3 is not decorative — see
 * `reconcileFromServer` for the cross-tab false-dirty defect they fix.
 */
export const TRANSITIONS: ReadonlyArray<{
  from: BoardEditPhase;
  to: BoardEditPhase;
  trigger: BoardEditTrigger;
}> = [
  { from: 'idle', to: 'pristine', trigger: 'STORE_LOADED' },
  { from: 'idle', to: 'closed', trigger: 'CLOSE_REQUESTED' },
  { from: 'pristine', to: 'dirty', trigger: 'FIELD_PATCHED' },
  { from: 'pristine', to: 'pristine', trigger: 'RECONCILED' },
  { from: 'pristine', to: 'closed', trigger: 'CLOSE_REQUESTED' },
  { from: 'dirty', to: 'pristine', trigger: 'FIELD_PATCHED' },
  { from: 'dirty', to: 'pristine', trigger: 'COMMIT_KEPT' },
  { from: 'dirty', to: 'dirty', trigger: 'RECONCILED' },
  { from: 'dirty', to: 'confirming', trigger: 'CLOSE_REQUESTED' },
  { from: 'dirty', to: 'reverting', trigger: 'DISCARD_REQUESTED' },
  { from: 'confirming', to: 'dirty', trigger: 'KEEP_EDITING' },
  { from: 'confirming', to: 'closed', trigger: 'KEEP_AND_CLOSE' },
  { from: 'confirming', to: 'reverting', trigger: 'DISCARD_AND_CLOSE_REQUESTED' },
  { from: 'reverting', to: 'pristine', trigger: 'REVERT_SUCCEEDED' },
  { from: 'reverting', to: 'closed', trigger: 'REVERT_SUCCEEDED_CLOSING' },
  { from: 'reverting', to: 'dirty', trigger: 'REVERT_FAILED' },
];

/** Resolve (from, trigger) → next phase, or null when the matrix forbids it. */
export function nextPhase(from: BoardEditPhase, trigger: BoardEditTrigger): BoardEditPhase | null {
  const row = TRANSITIONS.find((t) => t.from === from && t.trigger === trigger);
  return row ? row.to : null;
}

/** Board-edit session region. NEVER persisted — see the partializer note. */
interface BoardEditState {
  /** Current machine phase. Components RENDER this; they never own it. */
  boardPhase: BoardEditPhase;
  /**
   * The canonical discard target: the memo slice as it stood when this edit
   * session captured its baseline. `null` before STORE_LOADED.
   */
  boardSnapshot: BackendMemoSnapshot | null;
  /**
   * Records which intent started an in-flight revert, so the `reverting` phase
   * knows whether success means REVERT_SUCCEEDED (stay open, back to pristine)
   * or REVERT_SUCCEEDED_CLOSING (the "Discard & Close" path).
   */
  boardClosePending: boolean;
  /**
   * Bumped after a revert applies, to remount the uncontrolled `defaultValue`
   * number-input grids so their displayed value re-syncs from the restored store.
   */
  boardRemountKey: number;
  /** Transient board-level notice ("Changes kept…", "Revert failed…"). */
  boardNotice: { msg: string; ok: boolean } | null;
}

interface SettingsStore extends UserPreferences, DirectColumnSettings, LocalSettings, ServerOnlyState, BoardEditState {
  /** Phase 0 placeholder — retained so SE-SZ0 still passes and the dev probe
   *  still flushes uSES. Retires in Phase 3 with the dev probe. */
  _placeholder: number;
  bumpPlaceholder: () => void;
  /** Set profile-derived voice routing on profile load (App.tsx). Transient. SE-PF7. */
  setProfileVoice: (ttsProvider: string, whisperEnabled: boolean) => void;

  // ── BackendSettings actions (Phase 1) ──
  loadFromBackend: () => Promise<void>;
  patchMemo: (partial: Partial<WhitelistedMemo>) => Promise<void>;
  /** Phase 3b — restore the whole memo slice to a snapshot taken at modal-open,
   *  setting the store synchronously then PUT'ing the snapshot to /settings/memo
   *  so the server agrees with the restored store. Used by Discard. */
  restoreMemoSnapshot: (snapshot: BackendMemoSnapshot) => Promise<void>;

  // ── Board-edit session actions (R-A2: named actions, never CustomEvent) ──
  /** Modal mounted — start a fresh session at `idle`, discarding any prior snapshot. */
  beginEditSession: () => void;
  /** Modal unmounted — tear the session down so the next open starts clean. */
  endEditSession: () => void;
  /** ✕ / overlay click / swipe-down / Escape. Returns true when the caller may
   *  actually close; false means the machine raised the unsaved-changes confirm. */
  requestClose: () => boolean;
  /** Confirm dialog → "Keep Editing". */
  keepEditing: () => void;
  /** Confirm dialog → "Keep & Close" (values already live on the server). */
  keepAndClose: () => void;
  /** Save bar → "Save": re-baseline the snapshot; nothing is sent (live-apply). */
  commitKept: () => void;
  /** Save bar → "Discard": revert to the snapshot, stay open. */
  discardChanges: () => Promise<void>;
  /** Confirm dialog → "Discard & Close": revert AND end the session, atomically. */
  discardAndClose: () => Promise<boolean>;
  /** Dismiss the transient board notice. */
  dismissBoardNotice: () => void;
  /** Element 3 — reconcile the session against freshly-loaded server truth.
   *  Called by `loadFromBackend` on every WsEvent.PREFERENCES_UPDATED fan-out. */
  reconcileFromServer: () => void;
  setGeminiDeepMode: (mode: 'off' | 'detected' | 'all') => Promise<void>;
  setSttCorrectionEnabled: (enabled: boolean | null) => Promise<void>;
  setSttCorrectionThinking: (enabled: boolean | null) => Promise<void>;
  setPreferWhisper: (value: boolean | null) => Promise<void>;
  setAutoSubmitVoice: (value: boolean | null) => Promise<void>;
  setBrowserAlwaysOn: (enabled: boolean) => Promise<void>;
  recordConsent: (c: { tos?: boolean; privacy?: boolean; agentAccess?: boolean }) => Promise<void>;
  clearError: () => void;

  // ── LocalSettings actions (Phase 2) ──
  setTheme: (v: BratoTheme) => void;
  setDesktopDensity: (v: DesktopDensity) => void;
  setReducedMotion: (v: ReducedMotionPref) => void;
  setTtsVoiceId: (id: string | null) => void;
  setTtsSpeed: (speed: number) => void;
  setSttLanguage: (lang: string) => void;
  setReaderCodeBlocks: (mode: ReaderCodeBlocksMode) => void;
  setReaderCompact: (v: boolean) => void;
  setReaderSyntaxHighlight: (v: boolean) => void;
  setReaderFilePaths: (v: boolean) => void;
  setReaderSlashCmds: (v: boolean) => void;
  setReaderEmbedMedia: (v: boolean) => void;
  setBeetleVisible: (v: boolean) => void;
  setWasmEnabled: (v: boolean) => void;
}

// ── Internal helpers ────────────────────────────────────────────────────────

function pickWhitelistedMemo(partial: Partial<WhitelistedMemo>): Partial<WhitelistedMemo> {
  const out: Partial<WhitelistedMemo> = {};
  for (const key of BACKEND_MEMO_WHITELIST) {
    if (key in partial) {
      (out as Record<string, unknown>)[key] = (partial as Record<string, unknown>)[key];
    }
  }
  return out;
}

// ── Pre-rehydration legacy migration (R3) ───────────────────────────────────

export const LOCAL_SETTINGS_STORAGE_KEY = 'va-settings-v1';
export const LOCAL_SETTINGS_VERSION = 1;

/**
 * Read an arbitrary legacy localStorage key with try/catch — SSR-safe and
 * resilient to private-mode browsers that throw on access.
 */
function readLegacy(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

/** Convert legacy storage representations into the typed slice. */
function buildMigratedLocalSettings(): Partial<LocalSettings> {
  const out: Partial<LocalSettings> = {};

  // theme: prefer brato-theme, fall back to valko-theme (old brand name).
  const brato = readLegacy('brato-theme');
  const valko = readLegacy('valko-theme');
  if (brato || valko) out.theme = brato ?? valko;

  const voice = readLegacy('voice-assistant-voice');
  if (voice !== null) out.ttsVoiceId = voice;

  const speedRaw = readLegacy('voice-assistant-tts-speed');
  if (speedRaw !== null) {
    const parsed = parseFloat(speedRaw);
    if (!isNaN(parsed)) out.ttsSpeed = parsed;
  }

  const sttLang = readLegacy('stt-language');
  if (sttLang) out.sttLanguage = sttLang;

  const codeBlocks = readLegacy('reader-mode:code-blocks');
  if (codeBlocks === 'skip' || codeBlocks === 'announce' || codeBlocks === 'full') {
    out.readerCodeBlocks = codeBlocks;
  }

  // Reader booleans: legacy used `'false'` to mean disabled; absence/anything-else = enabled.
  const readerCompact = readLegacy('reader:compactSystem');
  if (readerCompact !== null) out.readerCompact = readerCompact !== 'false';
  const readerSyntax = readLegacy('reader:syntaxHighlight');
  if (readerSyntax !== null) out.readerSyntaxHighlight = readerSyntax !== 'false';
  const readerPaths = readLegacy('reader:filePaths');
  if (readerPaths !== null) out.readerFilePaths = readerPaths !== 'false';
  const readerSlash = readLegacy('reader:slashCmds');
  if (readerSlash !== null) out.readerSlashCmds = readerSlash !== 'false';
  const readerMedia = readLegacy('reader:embedMedia');
  if (readerMedia !== null) out.readerEmbedMedia = readerMedia !== 'false';

  const beetle = readLegacy('logbook-beetle-visible');
  if (beetle !== null) out.beetleVisible = beetle !== 'false';

  const wasm = readLegacy('valko-wasm-enabled');
  if (wasm !== null) out.wasmEnabled = wasm === 'true';

  return out;
}

/**
 * Run BEFORE `create()` is invoked so the persist middleware finds the seeded
 * blob on its first read. Idempotent: if `va-settings-v1` already exists, it is
 * NOT overwritten — user's most recent state wins.
 */
function runLegacyMigration(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (localStorage.getItem(LOCAL_SETTINGS_STORAGE_KEY)) return;
    const migrated = buildMigratedLocalSettings();
    if (Object.keys(migrated).length === 0) return;
    // Match the exact shape `persist` writes: { state, version }.
    const blob = JSON.stringify({ state: migrated, version: LOCAL_SETTINGS_VERSION });
    localStorage.setItem(LOCAL_SETTINGS_STORAGE_KEY, blob);
  } catch {
    /* localStorage may throw under quota pressure or private mode — skip gracefully */
  }
}

runLegacyMigration();

// ── Store ───────────────────────────────────────────────────────────────────

export const useSettingsStore = create<SettingsStore>()(
  devtools(
    persist(
      subscribeWithSelector((set, get) => {
        const runAction = async (actionName: string, fn: () => Promise<void>): Promise<void> => {
          set({ _saving: true, _lastError: null }, false, `${actionName}:start`);
          try {
            await fn();
            set({ _saving: false }, false, `${actionName}:ok`);
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            set({ _saving: false, _lastError: message }, false, `${actionName}:error`);
            throw err;
          }
        };

        /**
         * The ONLY way `boardPhase` changes. Rejects any (from, trigger) pair the
         * TRANSITIONS matrix doesn't list — an unlisted transition is a no-op plus
         * a DEV warning, never a silent state change. This is what makes the matrix
         * load-bearing (R-A1) instead of a comment that drifts from the code.
         */
        const applyPhase = (
          trigger: BoardEditTrigger,
          extra?: Partial<SettingsStore>,
        ): BoardEditPhase | null => {
          const from = get().boardPhase;
          const to = nextPhase(from, trigger);
          if (to === null) {
            if (import.meta.env.DEV) {
              console.warn(
                `[settings.store] board-edit FSM rejected ${from} --${trigger}--> (not in TRANSITIONS)`,
              );
            }
            return null;
          }
          set(
            { boardPhase: to, ...(extra ?? {}) } as Partial<SettingsStore>,
            false,
            `board:${trigger}:${from}->${to}`,
          );
          return to;
        };

        /** Dirty = any whitelisted key differs from the captured baseline. */
        const diffsFromSnapshot = (): boolean => {
          const { boardSnapshot } = get();
          if (!boardSnapshot) return false;
          const live = selectMemoSnapshotFields(get());
          for (const k of Object.keys(boardSnapshot) as Array<keyof BackendMemoSnapshot>) {
            if (!Object.is(boardSnapshot[k], live[k])) return true;
          }
          return false;
        };

        /**
         * Re-derive the phase after the memo values moved. `FIELD_PATCHED` is the
         * one trigger whose destination is a function of the diff, which is what
         * makes `dirty` a derived state rather than a latch: editing a field back
         * to its baseline value returns to `pristine` on its own.
         */
        const settleDirty = () => {
          const phase = get().boardPhase;
          if (phase !== 'pristine' && phase !== 'dirty') return;
          const shouldBe: BoardEditPhase = diffsFromSnapshot() ? 'dirty' : 'pristine';
          if (shouldBe !== phase) applyPhase('FIELD_PATCHED');
        };

        return {
          // ── Initial state ──
          ...DEFAULT_USER_PREFERENCES,
          ...DIRECT_COLUMN_DEFAULTS,
          ...LOCAL_SETTINGS_DEFAULTS,
          _loaded: false,
          _saving: false,
          _lastError: null,
          ttsProvider: 'piper',
          whisperEnabled: true,
          autoTurnBudgetHint: null,
          _placeholder: 0,
          bumpPlaceholder: () =>
            set((s) => ({ _placeholder: s._placeholder + 1 }), false, 'bumpPlaceholder'),

          // ── Board-edit session (initial) ───────────────────────────────────
          boardPhase: 'idle' as BoardEditPhase,
          boardSnapshot: null,
          boardClosePending: false,
          boardRemountKey: 0,
          boardNotice: null,

          // ── BackendSettings actions ────────────────────────────────────────

          loadFromBackend: async () => {
            await runAction('loadFromBackend', async () => {
              const settings: UserSettings = await getSettings();
              set(
                {
                  ...settings.memoSettings,
                  browserAlwaysOn: settings.browserAlwaysOn ?? false,
                  geminiDeepMode: settings.geminiDeepMode ?? 'detected',
                  sttCorrectionEnabled: settings.sttCorrectionEnabled ?? null,
                  sttCorrectionThinking: settings.sttCorrectionThinking ?? null,
                  preferWhisper: settings.preferWhisper ?? null,
                  autoSubmitVoice: settings.autoSubmitVoice ?? null,
                  autoTurnBudgetHint: settings.autoTurnBudgetHint ?? null,
                  _loaded: true,
                },
                false,
                'loadFromBackend:populate',
              );
            });
            // Element 3 — server-authoritative reconciliation. This runs for BOTH
            // the modal's initial load AND every WsEvent.PREFERENCES_UPDATED
            // cross-tab fan-out, and the machine tells them apart by phase: at
            // `idle` the load is this session's baseline (STORE_LOADED); once a
            // session is under way it is a reconcile (RECONCILED).
            if (get().boardPhase === 'idle') {
              applyPhase('STORE_LOADED', {
                boardSnapshot: selectMemoSnapshotFields(get()),
              });
            } else {
              get().reconcileFromServer();
            }
          },

          patchMemo: async (partial) => {
            const safe = pickWhitelistedMemo(partial);
            if (Object.keys(safe).length === 0) return;
            set(safe as Partial<SettingsStore>, false, 'patchMemo:optimistic');
            // The machine derives dirty from the diff, so field handlers stay dumb:
            // they patch, the FSM decides whether that made the board dirty (or
            // clean again, when the user edits a field back to its baseline).
            settleDirty();
            await runAction('patchMemo', async () => {
              const updated = await saveUserPreferences(safe);
              set(updated as Partial<SettingsStore>, false, 'patchMemo:echo');
              settleDirty();
            });
          },

          restoreMemoSnapshot: async (snapshot) => {
            // Pick only whitelisted keys — defends against a snapshot built from
            // a wider object (e.g. raw UserPreferences) being passed here.
            const safe = pickWhitelistedMemo(snapshot);
            if (Object.keys(safe).length === 0) return;
            // Synchronously apply the snapshot so the UI reflects the revert
            // before the PUT round-trip.
            set(safe as Partial<SettingsStore>, false, 'restoreMemoSnapshot:apply');
            await runAction('restoreMemoSnapshot', async () => {
              const updated = await saveUserPreferences(safe);
              set(updated as Partial<SettingsStore>, false, 'restoreMemoSnapshot:echo');
            });
          },

          // ── Board-edit session actions ─────────────────────────────────────

          beginEditSession: () =>
            set(
              {
                boardPhase: 'idle',
                boardSnapshot: null,
                boardClosePending: false,
                boardNotice: null,
              },
              false,
              'board:beginEditSession',
            ),

          endEditSession: () =>
            set(
              {
                boardPhase: 'idle',
                boardSnapshot: null,
                boardClosePending: false,
                boardNotice: null,
              },
              false,
              'board:endEditSession',
            ),

          reconcileFromServer: () => {
            // The cross-tab false-dirty defect (plans/…-board-edit-fsm.md D-1):
            // the old code captured the snapshot once and NEVER re-baselined, so a
            // sibling tab's legitimate change moved `liveMemo` while the snapshot
            // stayed behind — this tab then showed "Unsaved changes" for an edit its
            // user never made, and its Discard would have reverted the OTHER tab's
            // change. The blanket rule couldn't express the distinction because
            // there was no phase to condition on. Now there is:
            //
            //   clean  → re-baseline onto server truth (nothing local to lose)
            //   dirty  → PRESERVE the snapshot (never move a mid-edit discard target)
            const phase = get().boardPhase;
            if (phase === 'pristine') {
              applyPhase('RECONCILED', { boardSnapshot: selectMemoSnapshotFields(get()) });
            } else if (phase === 'dirty') {
              applyPhase('RECONCILED');
            }
          },

          requestClose: () => {
            const phase = get().boardPhase;
            if (phase === 'dirty') {
              applyPhase('CLOSE_REQUESTED'); // → confirming; caller must NOT close
              return false;
            }
            // idle | pristine → closed. From `confirming` or `reverting` the matrix
            // has no CLOSE_REQUESTED row, so applyPhase no-ops and we refuse the
            // close — Escape while the confirm is up must not become a second,
            // quieter path to the outcome the confirm exists to make deliberate.
            return applyPhase('CLOSE_REQUESTED') === 'closed';
          },

          keepEditing: () => { applyPhase('KEEP_EDITING'); },

          keepAndClose: () => { applyPhase('KEEP_AND_CLOSE'); },

          commitKept: () => {
            // Live-apply: every field already PUT on change, so "Save" sends
            // nothing — it re-baselines the snapshot so "Unsaved changes" clears.
            applyPhase('COMMIT_KEPT', {
              boardSnapshot: selectMemoSnapshotFields(get()),
              boardNotice: { msg: 'Changes kept — settings apply as you edit', ok: true },
            });
          },

          discardChanges: async () => {
            const snapshot = get().boardSnapshot;
            if (!snapshot) return;
            if (applyPhase('DISCARD_REQUESTED', { boardClosePending: false }) === null) return;
            try {
              await get().restoreMemoSnapshot(snapshot);
              applyPhase('REVERT_SUCCEEDED', {
                boardSnapshot: selectMemoSnapshotFields(get()),
                boardRemountKey: get().boardRemountKey + 1,
                boardClosePending: false,
              });
            } catch {
              applyPhase('REVERT_FAILED', {
                boardNotice: { msg: 'Revert failed — try again', ok: false },
                boardClosePending: false,
              });
            }
          },

          discardAndClose: async () => {
            // Element 4 — ONE action for one user intent. "Discard & Close"
            // crosses two regions (restore the server values, end the session);
            // splitting it into two store calls would be a bug even consecutively.
            const snapshot = get().boardSnapshot;
            if (!snapshot) {
              // Nothing captured yet — there is nothing to revert, so honour the close.
              applyPhase('KEEP_AND_CLOSE');
              return true;
            }
            if (applyPhase('DISCARD_AND_CLOSE_REQUESTED', { boardClosePending: true }) === null) {
              return false;
            }
            try {
              await get().restoreMemoSnapshot(snapshot);
              applyPhase('REVERT_SUCCEEDED_CLOSING', { boardClosePending: false });
              return true;
            } catch {
              // Stay open on failure: closing here would strand the user with the
              // very edits they asked to drop still live on the server. The confirm
              // dialog steps aside and the dirty bar carries the error notice.
              applyPhase('REVERT_FAILED', {
                boardNotice: { msg: 'Revert failed — try again', ok: false },
                boardClosePending: false,
              });
              return false;
            }
          },

          dismissBoardNotice: () => set({ boardNotice: null }, false, 'board:dismissNotice'),

          setGeminiDeepMode: async (mode) => {
            set({ geminiDeepMode: mode }, false, 'setGeminiDeepMode:optimistic');
            await runAction('setGeminiDeepMode', () => apiSetGeminiDeepMode(mode));
          },

          setSttCorrectionEnabled: async (enabled) => {
            set({ sttCorrectionEnabled: enabled }, false, 'setSttCorrectionEnabled:optimistic');
            await runAction('setSttCorrectionEnabled', () => apiSetSttCorrectionEnabled(enabled));
          },

          setSttCorrectionThinking: async (enabled) => {
            set({ sttCorrectionThinking: enabled }, false, 'setSttCorrectionThinking:optimistic');
            await runAction('setSttCorrectionThinking', () => apiSetSttCorrectionThinking(enabled));
          },

          setPreferWhisper: async (value) => {
            set({ preferWhisper: value }, false, 'setPreferWhisper:optimistic');
            await runAction('setPreferWhisper', () => apiSetPreferWhisper(value));
          },

          setAutoSubmitVoice: async (value) => {
            set({ autoSubmitVoice: value }, false, 'setAutoSubmitVoice:optimistic');
            await runAction('setAutoSubmitVoice', () => apiSetAutoSubmitVoice(value));
          },

          setBrowserAlwaysOn: async (enabled) => {
            set({ browserAlwaysOn: enabled }, false, 'setBrowserAlwaysOn:optimistic');
            await runAction('setBrowserAlwaysOn', async () => {
              const res = await authFetch('/api/settings/browser-always-on', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled }),
              });
              if (!res.ok) throw new Error(`Failed to set browser-always-on: ${res.status}`);
            });
          },

          recordConsent: async (consents) => {
            await runAction('recordConsent', () => apiRecordConsent(consents));
            await get().loadFromBackend();
          },

          clearError: () => set({ _lastError: null }, false, 'clearError'),

          // ── LocalSettings actions (synchronous) ───────────────────────────
          // Each writes to the store; persist middleware mirrors to localStorage.
          // setTheme additionally mirrors to legacy `brato-theme` for themeSync.ts
          // compatibility — its boot-time read of `va-settings-v1` is parse-based
          // but a synchronous string read is faster, so legacy stays write-through.

          setTheme: (v) => {
            set({ theme: v }, false, 'setTheme');
            try {
              if (v) localStorage.setItem('brato-theme', v);
              else localStorage.removeItem('brato-theme');
            } catch {
              /* private mode / quota — store remains authoritative */
            }
          },
          setDesktopDensity: (v) => set({ desktopDensity: v }, false, 'setDesktopDensity'),
          setReducedMotion: (v) => set({ reducedMotion: v }, false, 'setReducedMotion'),
          setTtsVoiceId: (id) => set({ ttsVoiceId: id }, false, 'setTtsVoiceId'),
          setTtsSpeed: (speed) => set({ ttsSpeed: speed }, false, 'setTtsSpeed'),
          setSttLanguage: (lang) => set({ sttLanguage: lang }, false, 'setSttLanguage'),
          setReaderCodeBlocks: (mode) => set({ readerCodeBlocks: mode }, false, 'setReaderCodeBlocks'),
          setReaderCompact: (v) => set({ readerCompact: v }, false, 'setReaderCompact'),
          setReaderSyntaxHighlight: (v) =>
            set({ readerSyntaxHighlight: v }, false, 'setReaderSyntaxHighlight'),
          setReaderFilePaths: (v) => set({ readerFilePaths: v }, false, 'setReaderFilePaths'),
          setReaderSlashCmds: (v) => set({ readerSlashCmds: v }, false, 'setReaderSlashCmds'),
          setReaderEmbedMedia: (v) => set({ readerEmbedMedia: v }, false, 'setReaderEmbedMedia'),
          setBeetleVisible: (v) => set({ beetleVisible: v }, false, 'setBeetleVisible'),
          setWasmEnabled: (v) => set({ wasmEnabled: v }, false, 'setWasmEnabled'),
          setProfileVoice: (ttsProvider, whisperEnabled) =>
            set({ ttsProvider, whisperEnabled }, false, 'setProfileVoice'),
        };
      }),
      {
        name: LOCAL_SETTINGS_STORAGE_KEY,
        version: LOCAL_SETTINGS_VERSION,
        storage: createJSONStorage(() => localStorage),
        /**
         * Strict partializer — only LocalSettings keys are persisted.
         * BackendSettings is server-truth (would cause stale-flash on 2nd device);
         * status flags (`_loaded`/`_saving`/`_lastError`) are per-session ephemera.
         * The Phase 0 `_placeholder` is also excluded.
         *
         * The board-edit session region (`boardPhase`/`boardSnapshot`/…) is
         * ephemeral too, and its exclusion is DELIBERATE, not an oversight — see
         * plans/settingsmodal-board-edit-fsm.md DD-2. A *persisted* snapshot would
         * outlive the refresh that ended its edit session and become a stale
         * discard target: reopen hours later, press Discard, and the modal PUTs
         * hours-old values over newer server state. Because this partializer is a
         * strict allowlist, the region is excluded BY CONSTRUCTION. Asserted by
         * setpoint-evals/settings-board-fsm/SE-08-session-region-not-persisted.
         *
         * Adding a new persisted local field = one new line here. Forgetting to
         * add a field means it works in-memory but vanishes on reload — caught
         * by SE-SZ2's reload assertion.
         */
        partialize: (state): LocalSettings => ({
          theme: state.theme,
          desktopDensity: state.desktopDensity,
          reducedMotion: state.reducedMotion,
          ttsVoiceId: state.ttsVoiceId,
          ttsSpeed: state.ttsSpeed,
          sttLanguage: state.sttLanguage,
          readerCodeBlocks: state.readerCodeBlocks,
          readerCompact: state.readerCompact,
          readerSyntaxHighlight: state.readerSyntaxHighlight,
          readerFilePaths: state.readerFilePaths,
          readerSlashCmds: state.readerSlashCmds,
          readerEmbedMedia: state.readerEmbedMedia,
          beetleVisible: state.beetleVisible,
          wasmEnabled: state.wasmEnabled,
        }),
      },
    ),
    { name: 'settings.store', enabled: import.meta.env.DEV },
  ),
);

// DEV probe — same as Phase 0/1; retained for SE-SZ0 + diagnostic introspection.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __settings_store_probe?: typeof useSettingsStore }).__settings_store_probe =
    useSettingsStore;
}

/**
 * Build a snapshot of the backend memo slice from current store state.
 *
 * The board-edit machine calls this to capture and re-baseline `boardSnapshot`
 * — the canonical "discard target". It used to be stashed in a `useRef` inside
 * SettingsModal; the store owns it now (board-edit FSM wave).
 */
export function selectMemoSnapshotFields(state: SettingsStore): BackendMemoSnapshot {
  const out: BackendMemoSnapshot = {};
  for (const key of BACKEND_MEMO_WHITELIST) {
    (out as Record<string, unknown>)[key] = (state as unknown as Record<string, unknown>)[key];
  }
  return out;
}
